import React, { useState } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import {
    MdOutlineEdit,
    MdSearch,
    MdMerge,
    MdBlock,
    MdOutlinePowerSettingsNew,
    MdNavigateBefore,
    MdNavigateNext,
} from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import AddVendorDrawer from '~/components/Vendors/AddVendorDrawer';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import { FaSearch } from 'react-icons/fa';

type Vendor = {
    id: number;
    name: string;
    date: string;
    status: string;
    referrals: number;
};

const defaultData: Vendor[] = [
    {
        id: 1,
        name: 'Vendor 1',
        date: '1/1/24',
        status: 'Active',
        referrals: 10,
    },
    {
        id: 2,
        name: 'Vendor 2',
        date: '1/1/24',
        status: 'Inactive',
        referrals: 10,
    },
];

const columnHelper = createColumnHelper<Vendor>();

const columns = [
    columnHelper.accessor('name', {
        id: 'name',
        header: () => 'Name',
        cell: (info) => (
            <Link
                className='font-bold text-primary'
                to={`/vendors-organizations/${info.row.original.id}`}
            >
                {info.renderValue()}
            </Link>
        ),
    }),
    columnHelper.accessor('type', {
        header: () => 'Type',
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('date', {
        header: () => 'Date',
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('status', {
        header: () => 'Status',
        cell: (props) => (
            <div
                className={`inline-flex rounded-full px-3 py-1 text-sm hover:bg-opacity-90 ${
                    props.getValue() === 'Active'
                        ? 'bg-primary'
                        : props.getValue() === 'Inactive'
                        ? 'bg-whiten text-secondary'
                        : ''
                }`}
            >
                {props.renderValue()}
            </div>
        ),
    }),
    columnHelper.accessor('referrals', {
        header: () => 'Referrals',
        cell: (info) => info.renderValue(),
    }),
];

const Vendors: React.FC = () => {
    const [data, setData] = React.useState(() => [...defaultData]);
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [pageSize, setPageSize] = React.useState(50);
    const rerender = React.useReducer(() => ({}), {})[1];
    const [isAddVendorShowing, setIsAddVendorShowing] = useState(false);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <Breadcrumb pageName='Vendors &amp; Organizations' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex items-center gap-4'>
                        <div className='relative w-full min-w-[125px] max-w-[175px]'>
                            <input
                                type='text'
                                value={globalFilter}
                                onChange={(e) =>
                                    setGlobalFilter(e.target.value)
                                }
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                placeholder='Name'
                            />
                            <FaSearch
                                color='gray'
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                            />
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <select
                                name='region'
                                id='region'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                            >
                                <option value=''>Region</option>
                                <option value='1'>First Option</option>
                                <option value='2'>Second Option</option>
                            </select>
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <select
                                name='status'
                                id='status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                            >
                                <option value=''>Status</option>
                                <option value='1'>First Option</option>
                                <option value='2'>Second Option</option>
                            </select>
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <select
                                name='type'
                                id='type'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                            >
                                <option value=''>Type</option>
                                <option value='1'>First Option</option>
                                <option value='2'>Second Option</option>
                            </select>
                        </div>
                    </div>

                    <Link
                        onClick={() => setIsAddVendorShowing(true)}
                        to='#'
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                    >
                        <FaPlus />
                        Add Vendor
                    </Link>
                </div>
                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {data.length} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdSearch className='text-primary' size={18} />
                            NPI Lookup
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdBlock className='text-red' size={18} />
                            Deactivate
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlinePowerSettingsNew
                                className='text-primary'
                                size={18}
                            />
                            Reactivate
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                </span>
                                                <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                    <span className='inline-block'>
                                                        <svg
                                                            xmlns='http://www.w3.org/2000/svg'
                                                            width='17'
                                                            height='16'
                                                            viewBox='0 0 17 16'
                                                            fill='none'
                                                        >
                                                            <path
                                                                d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                                                fill='#131212'
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                    <th
                                        style={{
                                            width: '140px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    className='border-t border-stroke'
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={cell.id}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    ))}
                                    <td
                                        className='text-end'
                                        style={{
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    >
                                        <CampaignActionDropdown />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className='flex justify-between border-t border-stroke px-8 pt-5'>
                    <p className='font-light'>Showing {data.length} results</p>
                    <div className='flex'>
                        <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
                            <MdNavigateBefore size={20} />
                        </button>

                        <button className='mx-1 flex cursor-pointer items-center justify-center rounded-md bg-primary p-1 px-3 hover:bg-primary'>
                            1
                        </button>
                        <button className='mx-1 flex cursor-pointer items-center justify-center rounded-md p-1 px-3 hover:bg-primary'>
                            2
                        </button>
                        <button className='mx-1 flex cursor-pointer items-center justify-center rounded-md p-1 px-3 hover:bg-primary'>
                            3
                        </button>

                        <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
                            <MdNavigateNext size={20} />
                        </button>
                    </div>
                </div>
            </section>
            <AddVendorDrawer
                isOpen={isAddVendorShowing}
                setIsOpen={setIsAddVendorShowing}
            />
        </>
    );
};

export default Vendors;
